<script>
import {
  Emoji, Input, MultipleChoice, Open, Picture, Rating, ScaleMetric, Thumbs, Title, Tooltip, Icon,
} from '@/packages/ui/components';
import { QUESTION_TYPE } from '@/consts/survey';
import getUserContextByMetric from '@/utils/getUserContextByMetric';
import getScale from '@/utils/getScale';
import EditPicture from './EditPicture.vue';
import CompositeMetrics from './CompositeMetrics.vue';

export default {
  components: {
    CompositeMetrics,
    EditPicture,
    Emoji,
    Icon,
    Input,
    MultipleChoice,
    Open,
    Picture,
    Rating,
    ScaleMetric,
    Thumbs,
    Title,
    Tooltip,
  },
  props: {
    questionData: {
      default: () => ({}),
      type: Object,
    },
    color: {
      default: '',
      type: String,
    },
    lang: {
      default: 'pt_BR',
      type: String,
    },
    onSelectAnswer: {
      default: () => ({}),
      type: Function,
    },
    currentAnswer: {
      default: () => ({}),
      type: Object,
    },
    isEdit: {
      default: false,
      type: Boolean,
    },
    error: {
      default: false,
      type: Boolean,
    },
  },
  data() {
    return {
      answer: null,
      comment: '',
      context: null,
      debounceInputCommentTimeout: null,
      debounceOtherOptionTimeout: null,
      other: '',
      otherJumpRule: [],
      scale: null,
      components: {
        [QUESTION_TYPE.LABEL]: 'Title',
        [QUESTION_TYPE.IMAGE]: this.isEdit ? 'EditPicture' : 'Picture',
        [QUESTION_TYPE.RATINGS]: 'rating',
        [QUESTION_TYPE.NPS_RELATIONAL]: 'scale-metric',
        [QUESTION_TYPE.NPS_TRANSACTIONAL]: 'scale-metric',
        [QUESTION_TYPE.CSAT]: 'scale-metric',
        [QUESTION_TYPE.CES]: 'scale-metric',
        [QUESTION_TYPE.CES2]: 'scale-metric',
        [QUESTION_TYPE.EMOJI]: 'emoji',
        [QUESTION_TYPE.MULTIPLE_CHOICE]: 'multiple-choice',
        [QUESTION_TYPE.OPEN]: 'open',
        [QUESTION_TYPE.THUMBS]: 'thumbs',
      },
    };
  },
  computed: {
    questionText() {
      return this.questionData.question?.[this.lang] || '';
    },
    questionId() {
      return this.questionData.item_uuid || this.questionData.id;
    },
    labelComment() {
      /* eslint-disable */
      const {
        comment_question,
        detractor_comment_question,
        neutral_comment_question,
        promoter_comment_question,
        type,
      } = this.questionData;

      if (type === this.$consts.QUESTION_TYPE.OPEN || type === this.$consts.QUESTION_TYPE.IMAGE) {
        return '';
      }

      switch (this.context) {
        case 'detractor':
          return detractor_comment_question?.[this.lang] || comment_question?.[this.lang] || '';
        case 'passive':
          return neutral_comment_question?.[this.lang] || comment_question?.[this.lang] || '';
        default:
          return promoter_comment_question?.[this.lang] || comment_question?.[this.lang] || '';
      }
      /* eslint-enable */
    },
    labelMin() {
      return this.questionData.button_subtitle_min?.[this.lang] || '';
    },
    labelMax() {
      return this.questionData.button_subtitle_max?.[this.lang] || '';
    },
    isMultipleChoice() {
      return this.questionData.type === this.$consts.QUESTION_TYPE.MULTIPLE_CHOICE;
    },
    isOpen() {
      return this.questionData.type === this.$consts.QUESTION_TYPE.OPEN;
    },
    isTitle() {
      return this.questionData.type === this.$consts.QUESTION_TYPE.LABEL;
    },
    scales() {
      return getScale(
        this.questionData.min_scale,
        this.questionData.max_scale,
        this.questionData.inverted_scale,
        this.questionData.scale_type,
        this.questionData.type,
        this.lang,
      );
    },
    hasCommentsEnabled() {
      if (!this.hasCurrentAnswer()) {
        return false;
      }
      return this.questionData.comments_enabled;
    },
    questionColor() {
      return `color: ${this.color}`;
    },
  },
  watch: {
    'questionData.multiple_answers': {
      handler() {
        this.answer = [];
        this.other = '';
      },
    },
    'questionData.randomize': {
      handler() {
        this.answer = [];
        this.other = '';
      },
    },
  },
  mounted() {
    if (this.currentAnswer?.answer?.comment) {
      this.handleCommentChange(this.currentAnswer.answer.comment);
    }
    if (this.currentAnswer?.answer?.response != null) {
      this.handleValueChange(this.currentAnswer.answer.response);
    }
    if (this.currentAnswer?.option_other) {
      this.handleOtherChange(this.currentAnswer.option_other);
    }
  },
  methods: {
    hasCurrentAnswer() {
      return (
        (Array.isArray(this.answer) && this.answer.length > 0)
        || (!Array.isArray(this.answer) && this.answer !== null)
        || this.other
      );
    },
    handleClearComment() {
      this.handleCommentChange('');
    },
    handleValueChange(value) {
      if (this.isMultipleChoice) {
        this.handleMultipleChoice(value);
        this.handleResponseChange();
      } else if (this.isOpen) {
        this.$emit('openAnswer', {
          value,
          uuid: this.questionData.item_uuid,
        });
        this.handleCommentChangeDebounce(value);
      } else {
        this.answer = value;
        this.context = getUserContextByMetric(
          this.questionData.type,
          value,
          this.questionData.min_scale,
          this.questionData.max_scale,
        );
        this.handleResponseChange();
      }
    },
    handleMultipleChoice(option) {
      let optionArray = [];
      if (!Array.isArray(option)) {
        optionArray = [{ ...option }];
      } else {
        optionArray = [...option];
      }

      if (!this.answer) {
        this.answer = optionArray;
        if (!this.questionData.multiple_answers) {
          this.other = '';
        }
      } else if (this.answer.find((a) => a[this.lang] === option[this.lang])) {
        this.answer = this.answer.filter((a) => a[this.lang] !== option[this.lang]);
      } else if (this.questionData.multiple_answers) {
        this.answer = this.answer.concat(option);
      } else {
        this.answer = optionArray;
        this.other = '';
      }
    },
    handleOtherChange(value, skipDebounce = false) {
      const interval = 1000;

      this.other = value;
      if (this.questionData.jump_rules_enabled && this.questionData.other_options_jump_rules) {
        this.otherJumpRule = this.questionData.other_options_jump_rules;
      }
      if (!this.questionData.multiple_answers) {
        this.answer = [];
      }

      clearTimeout(this.debounceOtherOptionTimeout);
      if (skipDebounce) {
        this.handleResponseChange();
      } else {
        this.debounceOtherOptionTimeout = setTimeout(() => {
          this.handleResponseChange();
        }, interval);
      }
    },
    handleCommentChangeDebounce(value) {
      this.comment = value;
      const interval = 1000;

      clearTimeout(this.debounceInputCommentTimeout);
      this.debounceInputCommentTimeout = setTimeout(() => {
        this.handleCommentChange(value);
      }, interval);
    },
    handleCommentChange(value) {
      clearTimeout(this.debounceInputCommentTimeout);
      this.comment = value;
      this.handleResponseChange();
    },
    handleResponseChange() {
      this.onSelectAnswer({
        uuid: this.questionId,
        type: this.questionData.type,
        answer: {
          response: this.answer,
          comment: this.comment,
          context: this.context,
          option_other: this.other,
          option_other_jump_rule: this.otherJumpRule,
        },
      });
    },
  },
  i18n: {
    messages: {
      en_US: {
        message_obligatory_comment: 'Please provide an answer and explanation for this question.',
      },
      es_ES: {
        message_obligatory_comment: 'Proporcione una respuesta y una explicación para esta pregunta.',
      },
      pt_BR: {
        message_obligatory_comment: 'Por favor, forneça uma resposta e explicação para esta pergunta. ',
      },
    },
  },
};
</script>

<template>
  <div class="cxm-SurveyQuestion">
    <div
      v-if="!isTitle"
      class="cxm-SurveyQuestion-Title"
      data-testid="question-title"
      :style="questionColor"
    >
      {{ questionText }}
      <span
        v-show="questionData.is_obligatory"
        class="cxm-SurveyQuestion-Obligatory"
      >
        <Tooltip :text="$t('survey.tooltip.obligatory')">*</Tooltip>
      </span>
    </div>
    <div class="cxm-SurveyQuestion-Metric">
      <component
        :is="components[questionData.type]"
        :key="questionId"
        :answer="isOpen ? comment : answer"
        :color="color"
        :comment-placeholder="$t('survey.new.placeholder_answer')"
        :handle-clear-comment="handleClearComment"
        :handle-other-change="handleOtherChange"
        :label-comment="labelComment"
        :label-max="labelMax"
        :label-min="labelMin"
        :language="lang"
        :other-label="$t('survey.new.other')"
        :other="other"
        :question-data="questionData"
        :question-text="questionText"
        :scales="scales"
        @change="handleValueChange"
      />
    </div>
    <CompositeMetrics
      class="cxm-SurveyQuestion-CompositionMetric"
      :question-data="questionData"
      :lang="lang"
      :color="color"
      :on-select-answer="onSelectAnswer"
      :answer="answer"
    />
    <div
      v-show="hasCommentsEnabled"
      class="cxm-SurveyQuestion-Comment"
    >
      <Input
        :label="labelComment"
        type="textarea"
        autogrow
        :placeholder="$t('survey.new.placeholder_answer')"
        maxlength="5000"
        :value="comment"
        @input="handleCommentChangeDebounce"
      />
      <div
        v-if="error"
        class="cxm-SurveyQuestion-ErrorComment"
      >
        <Icon
          name="NewError"
          size="20"
          color="#DE6262"
        />
        {{ $t('message_obligatory_comment') }}
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.cxm-SurveyQuestion {
  &-Obligatory {
    font-size: var(--track-font-size-xl);
    color: var(--track-color-negative-medium);
    margin-left: var(--track-spacing-xs);
  }

  &-Title {
    font-size: var(--track-font-size-sm);
    color: var(--track-color-bluegray-70);
    font-weight: var(--track-font-weight-medium);
    line-height: 23px;
    display: flex;

    @media screen and (max-width: 1024px) {
      font-size: var(--track-font-size-xs);
    }
  }

  &-Metric {
    margin-top: var(--track-spacing-md);
    display: flex;
    justify-content: center;

    @media screen and (max-width: 1024px) {
      margin-top: var(--track-spacing-sm);
    }
  }

  &-CompositionMetric, &-Comment {
    margin-top: var(--track-spacing-md);
  }
  .cxm-SurveyQuestion-ErrorComment {
    display: flex;
    align-items: center;
    gap: var(--track-spacing-xs);
    margin-top: var(--track-spacing-sm);
    font-size: var(--track-font-size-xs);
    font-weight: var(--track-font-weight-regular);
    color: #DE6262;
  }
}
</style>
